import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Card, Col, message, Progress, Row, Spin, Typography, Upload,} from "antd";
import {UploadOutlined} from "@ant-design/icons";
import Layout from "../Layout";

const {Title, Text} = Typography;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.21stcenturyav.info";

const UpdateDescriptionsPage = () => {
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [uploadProgress, setUploadProgress] = useState({
        progress: 0,
        status: "",
        total: 0,
        processed: 0,
    });

    const accessToken = localStorage.getItem("accessToken");
    const pollingRef = useRef(null);

    useEffect(() => {
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, []);

    const pollTaskProgress = (taskId) => {
        pollingRef.current = setInterval(async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/update_description_progress/${taskId}`, {
                    headers: {Authorization: `Bearer ${accessToken}`},
                });
                if (!response.ok) throw new Error("Failed to fetch progress");
                const progressData = await response.json();
                setUploadProgress(progressData);
                if (["completed", "failed"].includes(progressData.status)) {
                    clearInterval(pollingRef.current);
                }
            } catch (err) {
                console.error("Polling error:", err.message);
                clearInterval(pollingRef.current);
            }
        }, 2000);
    };

    const handleUpload = async () => {
        if (!fileList.length) {
            message.warning("Please select a CSV file.");
            return;
        }

        const formData = new FormData();
        formData.append("file", fileList[0]);

        try {
            setUploading(true);
            const response = await fetch(`${API_BASE_URL}/update_description_csv`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.detail || "Upload failed");
            }

            const data = await response.json();
            setTaskId(data.task_id);
            message.success(data.message || "Description update started.");
            setFileList([]);
            pollTaskProgress(data.task_id);
        } catch (err) {
            console.error("Upload error:", err.message);
            message.error(err.message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Layout pageTitle="Update Product Descriptions">
            <div style={{padding: "20px", maxWidth: "1200px", margin: "0 auto"}}>
                <Spin spinning={uploading} tip="Processing CSV..." size="large">
                    <Card
                        title={
                            <Title level={3} style={{margin: 0}}>
                                <UploadOutlined style={{marginRight: 8}}/>
                                Update Product Descriptions via CSV
                            </Title>
                        }
                        extra={
                            <Button
                                type="primary"
                                onClick={handleUpload}
                                disabled={!fileList.length || uploading}
                                loading={uploading}
                            >
                                {uploading ? "Uploading..." : "Start Upload"}
                            </Button>
                        }
                    >
                        <Row gutter={16}>
                            <Col span={24}>
                                <Upload.Dragger
                                    accept=".csv"
                                    beforeUpload={(file) => {
                                        setFileList([file]);
                                        return false;
                                    }}
                                    fileList={fileList}
                                    onRemove={() => setFileList([])}
                                    showUploadList={{showRemoveIcon: true}}
                                    multiple={false}
                                >
                                    <div style={{padding: "40px 20px"}}>
                                        <p style={{fontSize: 16}}>
                                            <UploadOutlined style={{fontSize: 24}}/>
                                        </p>
                                        <p>
                                            {fileList.length ? fileList[0].name : "Click or drag CSV file here"}
                                        </p>
                                    </div>
                                </Upload.Dragger>
                            </Col>
                        </Row>

                        <div style={{marginTop: 24}}>
                            <Alert
                                message="CSV Format Requirements"
                                description={
                                    <>
                                        <Text strong>Required Columns:</Text> GTIN, Description
                                        <br/>
                                        <Text type="secondary">
                                            GTIN is used to find the product. Description will be updated on
                                            WooCommerce/WordPress.
                                        </Text>
                                    </>
                                }
                                type="info"
                                showIcon
                            />
                            <div style={{marginTop: 16}}>
                                <Text strong>Example CSV:</Text>
                                <pre
                                    style={{
                                        background: "#f6f8fa",
                                        padding: 16,
                                        borderRadius: 4,
                                        marginTop: 8,
                                        overflowX: "auto",
                                    }}
                                >
{`GTIN,Description
"1234567890123","Updated description for product A"
"9876543210987","Another description for product B"`}
                </pre>
                            </div>
                        </div>
                    </Card>

                    {taskId && (
                        <Card title={<Title level={4}>Update Progress</Title>} style={{marginTop: 24}}>
                            <Alert
                                message="Description Update Progress"
                                description={
                                    <>
                                        <Text strong>Status:</Text>{" "}
                                        {uploadProgress.status?.toUpperCase() || "N/A"}
                                        <br/>
                                        <Text
                                            strong>Processed:</Text> {uploadProgress.processed} / {uploadProgress.total}
                                    </>
                                }
                                type={uploadProgress.status === "failed" ? "error" : "info"}
                                showIcon
                            />
                            <Progress
                                percent={uploadProgress.progress}
                                status={
                                    uploadProgress.status === "failed"
                                        ? "exception"
                                        : uploadProgress.status === "completed"
                                            ? "success"
                                            : "active"
                                }
                            />
                        </Card>
                    )}
                </Spin>
            </div>
        </Layout>
    );
};

export default UpdateDescriptionsPage;
